import { noop } from 'lodash';
import type { ResultOf } from '@graphql-typed-document-node/core';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { checkIfIsArray } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType, ProjectionId } from '@e-socna/data-layer';

import { N_A } from '../constants/labels.constant';
import { useFromAddressToLatLon } from '../forms/Address/hooks/useFromAddressToLatLon.hook';
import { useReferencesContext } from '../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { checkIfIsAddressEmpty } from '../forms/Address/utils/check-if-is-address-empty.util';

import { DarkMap } from './Illustrations/DarkMap';
import { LazyMap } from './Map/Lazy';
import { StateStrategy } from './Map/enums/state-strategy.enum';
import { PathStrategy } from './Map/enums/path-strategy.enum';
import type { SpeedDialAction } from './SpeedDial/types/action.type';
import { InfoAlert } from './InfoAlert';

export const AddressWithMapCmp_Address_Fragment = graphql(`
  fragment AddressWithMapCmp_Address_Fragment on Address {
    _id
    typeIds
    postalInfo {
      _id
      streetNumber
      route
      locality
      postalCode
      department
      region
      countryAlpha2
      building
      additional
      businessPostCode {
        postalCode
        distributorOfficeNumber
      }
      postOfficeBox
      inseeCode
    }
    locationInfo {
      _id
      coordinates
      projectionId
      altitude
    }
    cadastralInfo {
      _id
      departmentCode
      municipalityCode
      oldMunicipalityCode
      districtCode
      section
      number
      sheet
    }
  }
`);

export type AddressWithMapCmp_Address = ResultOf<
  typeof AddressWithMapCmp_Address_Fragment
>;

interface Props {
  readonly actionsEnabled?: boolean;
  readonly address:
    FragmentType<typeof AddressWithMapCmp_Address_Fragment> | undefined | null;
  readonly editAction?: SpeedDialAction;
  readonly fallbackAddress?:
    FragmentType<typeof AddressWithMapCmp_Address_Fragment> | undefined | null;
  readonly id: string;
  readonly onCustomize?: () => void;
  readonly onFlush?: () => void;
  readonly onStartCreate?: () => void;
  readonly sx?: SxProps<Theme>;
}

export const AddressWithMap = ({
  actionsEnabled = false,
  address: pAddress,
  editAction,
  fallbackAddress: pFallbackAddress,
  id,
  onCustomize: handleCustomize,
  onFlush: handleFlush,
  onStartCreate: handleStartCreate,
  sx,
}: Readonly<Props>) => {
  const address = getFragmentData(AddressWithMapCmp_Address_Fragment, pAddress);
  const fallbackAddress = getFragmentData(
    AddressWithMapCmp_Address_Fragment,
    pFallbackAddress,
  );

  const noAddress = checkIfIsAddressEmpty(address);
  const canInherit = !!fallbackAddress;
  const mustInherit = canInherit && noAddress;
  const addressOrFallback = mustInherit ? fallbackAddress : address;
  const noAddressOrFallback = checkIfIsAddressEmpty(addressOrFallback);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));

  const { data: latLon } = useFromAddressToLatLon({
    address: addressOrFallback,
  });

  const { projectionsIdMap, projectionsCodeMap } = useReferencesContext();

  if (noAddressOrFallback) {
    return (
      <Grid
        container
        sx={[
          {
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            alignItems: 'center',
          },
          ...(!sx ? EMPTY_ARRAY : checkIfIsArray(sx) ? sx : [sx]),
        ]}
      >
        <Grid
          size={{
            lg: 6,
            xs: 12,
          }}
        >
          <DarkMap />
        </Grid>
        <Grid
          size={{
            lg: 6,
            xs: 12,
          }}
          sx={{
            ...(matches ? EMPTY_OBJECT : { minHeight: '150px' }),
            textAlign: 'center',
          }}
        >
          <Box>
            <Typography gutterBottom sx={{ mb: 2 }} variant="body2">
              Aucune adresse n&apos;a été définie pour le moment.
            </Typography>
            {!!(actionsEnabled && handleStartCreate) && (
              <Button
                onClick={handleStartCreate}
                size="large"
                variant="contained"
              >
                Spécifier l&apos;Adresse
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  }

  const { postalInfo, locationInfo, cadastralInfo } = addressOrFallback;

  const isLatLon =
    locationInfo?.projectionId === projectionsCodeMap.Epsg4326._id;

  return (
    <Grid
      container
      spacing={2}
      sx={[
        { display: 'flex', flexGrow: 1 },
        ...(!sx ? EMPTY_ARRAY : checkIfIsArray(sx) ? sx : [sx]),
      ]}
    >
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            height: !matches ? '400px' : '100%',
            width: '100%',
          }}
        >
          {!!latLon && (
            <LazyMap
              areControlsHidden
              editAction={editAction}
              id={`${id}-map`}
              initialPosition={latLon}
              isStatic
              pathStrategy={PathStrategy.Stateful}
              selectedFeature={{ position: latLon }}
              setSelectedFeature={noop}
              stateStrategy={StateStrategy.Controlled}
              useCase={{
                id: 'AddressWithMap',
                allowElementsList: false,
                description: 'Carte pour adresse',
              }}
            />
          )}
        </Box>
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <Stack spacing={2} sx={{ flexGrow: 1, overflow: 'auto' }}>
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
          {!!canInherit && (
            <InfoAlert
              action={
                <>
                  {!!(actionsEnabled && handleCustomize && handleFlush) && (
                    <Button
                      color="inherit"
                      onClick={mustInherit ? handleCustomize : handleFlush}
                      size="small"
                    >
                      {mustInherit ? 'Spécifier' : 'Réinitialiser'}
                    </Button>
                  )}
                </>
              }
              key={String(mustInherit)}
            >
              {mustInherit
                ? 'Cette adresse est pour le moment héritée.'
                : 'Cette adresse peut être héritée.'}
            </InfoAlert>
          )}
          {!!postalInfo && (
            <>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Typography gutterBottom variant="h6">
                    Informations Postales
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    Numéro : {postalInfo.streetNumber}
                    <br />
                    Rue : {postalInfo.route}
                    <br />
                    Code Postal : {postalInfo.postalCode}
                    <br />
                    Ville : {postalInfo.locality}
                    <br />
                    Code INSEE : {postalInfo.inseeCode}
                    <br />
                    Département : {postalInfo.department}
                    <br />
                    Région : {postalInfo.region}
                    <br />
                    Pays : {postalInfo.countryAlpha2}
                  </Typography>
                </Box>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Typography gutterBottom variant="h6">
                    Complément d&apos;adresse
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    Bâtiment : {postalInfo.building ?? N_A}
                    <br />
                    Informations Supplémentaires :{' '}
                    {postalInfo.additional ?? N_A}
                    <br />
                    CEDEX :{' '}
                    {!postalInfo.businessPostCode
                      ? N_A
                      : `${postalInfo.businessPostCode.postalCode} ${postalInfo.locality} CEDEX${
                          postalInfo.businessPostCode.distributorOfficeNumber
                            ? ` ${postalInfo.businessPostCode.distributorOfficeNumber}`
                            : EMPTY_STRING
                        }`}
                    <br />
                    Boîte Postale : {postalInfo.postOfficeBox ?? N_A}
                  </Typography>
                </Box>
              </Paper>
            </>
          )}
          {!!locationInfo && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Typography gutterBottom variant="h6">
                  Informations de Localisation
                </Typography>
                <Typography gutterBottom variant="body2">
                  Projection :{' '}
                  {projectionsIdMap[locationInfo.projectionId as ProjectionId]
                    ?.label ?? 'Inconnue'}
                  <br />
                  {isLatLon ? (
                    <>
                      Latitude : {locationInfo.coordinates[1]}
                      <br />
                      Longitude : {locationInfo.coordinates[0]}
                    </>
                  ) : (
                    <>Coordonnées : {locationInfo.coordinates.join(', ')}</>
                  )}
                  <br />
                  Altitude en mètres : {locationInfo.altitude}
                </Typography>
              </Box>
            </Paper>
          )}
          {!!cadastralInfo && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Typography gutterBottom variant="h6">
                  Informations Cadastrales
                </Typography>
                <Typography gutterBottom variant="body2">
                  Code INSEE Département : {cadastralInfo.departmentCode}
                  <br />
                  Code INSEE Commune : {cadastralInfo.municipalityCode}
                  <br />
                  Commune Absorbée : {cadastralInfo.oldMunicipalityCode}
                  <br />
                  Arrondissement : {cadastralInfo.districtCode}
                  <br />
                  Section : {cadastralInfo.section}
                  <br />
                  Numéro de parcelle : {cadastralInfo.number}
                  <br />
                  Feuille : {cadastralInfo.sheet}
                </Typography>
              </Box>
            </Paper>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};
